import { useState, useEffect, ReactNode } from 'react';
import { actividadAdminService, HierarchicalActividadesStats, LumisialActividadesGroup, FiltrosAdminActividades } from '../services/actividadAdminService';
import { AdminDetalleActividadModal } from '../components/admin/AdminDetalleActividadModal';
import { AdminFiltersPanel } from '../components/admin/AdminFiltersPanel';
import { ActividadesJAView } from '../components/admin/ActividadesJAView';
import { ActividadEconomica } from '../lib/supabase';
import { StatusBadge } from '../components/ui/StatusBadge';
import { formatCurrency, formatDate } from '../utils/formatters';
import { useAdminHierarchyFilters } from '../hooks/useAdminHierarchyFilters';
import { useToggleSet } from '../hooks/useToggleSet';
import { useAuth } from '../context/AuthContext';
import {
  Briefcase,
  ChevronDown,
  ChevronRight,
  ArrowUpCircle,
  ArrowDownCircle,
  TrendingUp,
  Building2,
  Hash,
} from 'lucide-react';

interface SelectedActividad {
  actividadId: string;
  lumisialId: string;
  lumisialNombre: string;
  lumisialCiudad: string;
  diocesisNombre: string;
  provinciaNombre: string;
}

const ESTADO_OPTIONS = [
  { value: 'planificacion', label: 'Planificación' },
  { value: 'en_curso', label: 'En Curso' },
  { value: 'finalizada', label: 'Finalizada' },
];

const GRID_COLS = '1fr 80px 90px 90px 90px';

const COLOR_POSITIVO = 'text-emerald-700';
const COLOR_NEGATIVO = 'text-red-600';

function utilidadColor(valor: number): string {
  return valor >= 0 ? COLOR_POSITIVO : COLOR_NEGATIVO;
}

type TabId = 'jad' | 'jan' | 'jads' | 'jans' | 'jals';

const TAB_LABELS: Record<TabId, string> = {
  jad: 'JAD',
  jan: 'JAN',
  jads: 'JADs',
  jans: 'JANs',
  jals: 'JALs',
};

// ---------------------------------------------------------------------------
// Subcomponentes para celdas de grid (evitan duplicación con AdminCampanasPage)
// ---------------------------------------------------------------------------

interface LumisialButtonContentProps {
  readonly nombre: string;
  readonly ciudad?: string | null;
  readonly isExpanded: boolean;
  readonly totalActividades: number;
  readonly totalIngresos: number;
  readonly totalCostos: number;
  readonly utilidadNeta: number;
}

function LumisialButtonContent({ nombre, ciudad, isExpanded, totalActividades, totalIngresos, totalCostos, utilidadNeta }: LumisialButtonContentProps) {
  return (
    <>
      <div className="flex items-center gap-2 py-3 pl-4 min-w-0">
        {isExpanded
          ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
          : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        }
        <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <div className="text-left min-w-0">
          <p className="font-semibold text-gray-800 text-sm truncate">{nombre}</p>
          {ciudad && <p className="text-xs text-gray-400 truncate">{ciudad}</p>}
        </div>
      </div>
      <div className="text-center py-3 pr-2">
        <span className="font-semibold text-gray-700 text-sm">{totalActividades}</span>
      </div>
      <div className="text-center py-3 pr-2">
        <span className="font-semibold text-emerald-700 text-sm">{formatCurrency(totalIngresos)}</span>
      </div>
      <div className="text-center py-3 pr-2">
        <span className="font-semibold text-red-600 text-sm">{formatCurrency(totalCostos)}</span>
      </div>
      <div className="text-center py-3 pr-4">
        <span className={`font-semibold text-sm ${utilidadNeta >= 0 ? COLOR_POSITIVO : COLOR_NEGATIVO}`}>
          {formatCurrency(utilidadNeta)}
        </span>
      </div>
    </>
  );
}

interface DiocesisNacionalButtonContentProps {
  readonly nombre: string;
  readonly tipo: string;
  readonly isExpanded: boolean;
  readonly totalActividades: number;
  readonly totalIngresos: number;
  readonly totalCostos: number;
  readonly utilidadNeta: number;
}

function DiocesisNacionalButtonContent({ nombre, tipo, isExpanded, totalActividades, totalIngresos, totalCostos, utilidadNeta }: DiocesisNacionalButtonContentProps) {
  return (
    <>
      <div className="flex items-center gap-2 py-3 pl-4 min-w-0">
        {isExpanded
          ? <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
          : <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
        }
        <div className="text-left min-w-0">
          <p className="text-xs text-gray-400">{tipo === 'diocesis' ? 'Diócesis' : 'Prefectura'}</p>
          <p className="font-bold text-gray-800 truncate">{nombre}</p>
        </div>
      </div>
      <div className="text-center py-3 pr-2">
        <p className="text-xs text-gray-400 flex items-center justify-center gap-0.5 mb-0.5"><Hash className="w-3 h-3" /></p>
        <span className="font-bold text-gray-900">{totalActividades}</span>
      </div>
      <div className="text-center py-3 pr-2">
        <p className="text-xs text-emerald-400 flex items-center justify-center gap-0.5 mb-0.5"><ArrowUpCircle className="w-3 h-3" /></p>
        <span className="font-bold text-emerald-700">{formatCurrency(totalIngresos)}</span>
      </div>
      <div className="text-center py-3 pr-2">
        <p className="text-xs text-red-400 flex items-center justify-center gap-0.5 mb-0.5"><ArrowDownCircle className="w-3 h-3" /></p>
        <span className="font-bold text-red-600">{formatCurrency(totalCostos)}</span>
      </div>
      <div className="text-center py-3 pr-4">
        <p className="text-xs text-blue-400 flex items-center justify-center gap-0.5 mb-0.5"><TrendingUp className="w-3 h-3" /></p>
        <span className={`font-bold ${utilidadNeta >= 0 ? COLOR_POSITIVO : COLOR_NEGATIVO}`}>
          {formatCurrency(utilidadNeta)}
        </span>
      </div>
    </>
  );
}

interface DiocesisGlobalButtonContentProps {
  readonly nombre: string;
  readonly tipo: string;
  readonly isExpanded: boolean;
  readonly totalActividades: number;
  readonly totalIngresos: number;
  readonly totalCostos: number;
  readonly utilidadNeta: number;
}

function DiocesisGlobalButtonContent({ nombre, tipo, isExpanded, totalActividades, totalIngresos, totalCostos, utilidadNeta }: DiocesisGlobalButtonContentProps) {
  return (
    <>
      <div className="flex items-center gap-2 py-2.5 pl-8 min-w-0">
        {isExpanded
          ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
          : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        }
        <span className="font-semibold text-gray-700 text-sm truncate">
          {tipo === 'diocesis' ? 'Diócesis' : 'Prefectura'} {nombre}
        </span>
      </div>
      <div className="text-center py-2.5 pr-2">
        <span className="font-semibold text-gray-700 text-sm">{totalActividades}</span>
      </div>
      <div className="text-center py-2.5 pr-2">
        <span className="font-semibold text-emerald-700 text-sm">{formatCurrency(totalIngresos)}</span>
      </div>
      <div className="text-center py-2.5 pr-2">
        <span className="font-semibold text-red-600 text-sm">{formatCurrency(totalCostos)}</span>
      </div>
      <div className="text-center py-2.5 pr-4">
        <span className={`font-semibold text-sm ${utilidadNeta >= 0 ? COLOR_POSITIVO : COLOR_NEGATIVO}`}>
          {formatCurrency(utilidadNeta)}
        </span>
      </div>
    </>
  );
}

export default function AdminActividadesPage() {
  const { usuario } = useAuth();
  const filters = useAdminHierarchyFilters();
  const { filterProvincia, filterDiocesis, filterLumisial, filterEstado, filterBusqueda, hasActiveFilters, isDiocesano, isNacional } = filters;

  let tabs: TabId[];
  if (isDiocesano) {
    tabs = ['jad', 'jals'];
  } else if (isNacional) {
    tabs = ['jan', 'jads', 'jals'];
  } else {
    tabs = ['jans', 'jads', 'jals'];
  }

  const [activeTab, setActiveTab] = useState<TabId>(tabs[0]);

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<HierarchicalActividadesStats>({ provincias: [] });
  const [selectedActividad, setSelectedActividad] = useState<SelectedActividad | null>(null);

  const { expanded: expandedProvincias, toggle: toggleProvincia } = useToggleSet();
  const { expanded: expandedDiocesis, toggle: toggleDiocesis } = useToggleSet();
  const { expanded: expandedLumisiales, toggle: toggleLumisial } = useToggleSet();

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const data = await actividadAdminService.getActividadesGrouped({
          provinciaEclesiasticaId: filterProvincia || undefined,
          diocesisId: filterDiocesis || undefined,
          lumisialId: filterLumisial || undefined,
          estado: (filterEstado as FiltrosAdminActividades['estado']) || undefined,
          busqueda: filterBusqueda || undefined,
        });
        setStats(data);
      } catch (error) {
        console.error('Error cargando actividades:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [filterProvincia, filterDiocesis, filterLumisial, filterEstado, filterBusqueda]);

  const handleSelectActividad = (
    actividad: ActividadEconomica,
    lumisial: LumisialActividadesGroup,
    diocesisNombre: string,
    provinciaNombre: string
  ) => {
    setSelectedActividad({
      actividadId: actividad.id,
      lumisialId: lumisial.lumisialId,
      lumisialNombre: lumisial.lumisialNombre,
      lumisialCiudad: lumisial.lumisialCiudad,
      diocesisNombre,
      provinciaNombre,
    });
  };

  const totalActividades = stats.provincias.reduce((s, p) => s + p.totalActividades, 0);
  const totalIngresos = stats.provincias.reduce((s, p) => s + p.totalIngresos, 0);
  const totalCostos = stats.provincias.reduce((s, p) => s + p.totalCostos, 0);
  const totalUtilidad = totalIngresos - totalCostos;

  const renderActividadRow = (
    actividad: ActividadEconomica,
    lumisial: LumisialActividadesGroup,
    diocesisNombre: string,
    provinciaNombre: string
  ) => {
    const utilidad = actividad.total_ingresos - actividad.total_costos;
    return (
      <div key={`act-${actividad.id}`} className="contents">
        <button
          onClick={() => handleSelectActividad(actividad, lumisial, diocesisNombre, provinciaNombre)}
          className="col-span-full grid hover:bg-blue-50 transition-colors text-left group border-t border-gray-100"
          style={{ gridTemplateColumns: GRID_COLS, alignItems: 'center' }}
        >
          <div className="flex items-center gap-3 py-3 pl-8 min-w-0">
            <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
              <Briefcase className="w-3.5 h-3.5 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-gray-800 text-sm truncate group-hover:text-blue-700 transition-colors">
                {actividad.nombre}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <StatusBadge estado={actividad.estado} tipo="actividad" />
                {actividad.fecha_inicio && (
                  <span className="text-xs text-gray-400">{formatDate(actividad.fecha_inicio)}</span>
                )}
              </div>
            </div>
          </div>
          <div className="text-center py-3 pr-2" />
          <div className="text-center py-3 pr-2">
            <span className="text-sm text-emerald-600 font-medium">{formatCurrency(actividad.total_ingresos)}</span>
          </div>
          <div className="text-center py-3 pr-2">
            <span className="text-sm text-red-500 font-medium">{formatCurrency(actividad.total_costos)}</span>
          </div>
          <div className="text-center py-3 pr-4">
            <span className={`text-sm font-bold ${utilidadColor(utilidad)}`}>
              {formatCurrency(utilidad)}
            </span>
          </div>
        </button>
      </div>
    );
  };

  const renderLumisialRows = (
    lumisial: LumisialActividadesGroup,
    diocesisNombre: string,
    provinciaNombre: string
  ) => {
    const rows = [(
      <div
        key={`lumisial-${lumisial.lumisialId}`}
        className="contents group"
      >
        <button
          onClick={() => toggleLumisial(lumisial.lumisialId)}
          className="col-span-full grid hover:bg-gray-50 transition-colors border-t border-gray-100 first:border-t-0"
          style={{ gridTemplateColumns: GRID_COLS, alignItems: 'center' }}
        >
          <LumisialButtonContent
            nombre={lumisial.lumisialNombre}
            ciudad={lumisial.lumisialCiudad}
            isExpanded={expandedLumisiales.has(lumisial.lumisialId)}
            totalActividades={lumisial.totalActividades}
            totalIngresos={lumisial.totalIngresos}
            totalCostos={lumisial.totalCostos}
            utilidadNeta={lumisial.utilidadNeta}
          />
        </button>
      </div>
    )];

    if (expandedLumisiales.has(lumisial.lumisialId)) {
      if (lumisial.actividades.length === 0) {
        rows.push(
          <div key={`empty-${lumisial.lumisialId}`} className="contents">
            <div className="col-span-full py-4 text-center text-gray-400 text-sm bg-gray-50/50">
              No hay actividades que coincidan con los filtros
            </div>
          </div>
        );
      } else {
        lumisial.actividades.forEach(actividad => {
          rows.push(renderActividadRow(actividad, lumisial, diocesisNombre, provinciaNombre));
        });
      }
    }

    return rows;
  };

  const renderActividadesGrid = (
    provinciaId: string | null,
    diocesisId: string | null
  ) => {
    const nodos: ReactNode[] = [];
    for (const p of stats.provincias) {
      if (provinciaId !== null && p.provinciaId !== provinciaId) continue;
      for (const d of p.diocesis) {
        if (diocesisId !== null && d.diocesisId !== diocesisId) continue;
        for (const l of d.lumisiales) {
          nodos.push(...renderLumisialRows(l, d.diocesisNombre, p.provinciaNombre));
        }
      }
    }
    return nodos;
  };

  const renderDiocesisCardNacional = (d: typeof stats.provincias[0]['diocesis'][0], p: typeof stats.provincias[0]) => (
    <div key={d.diocesisId} className="border border-gray-200 rounded-lg overflow-hidden">
      <div
        className="grid"
        style={{ gridTemplateColumns: GRID_COLS }}
      >
        <button
          onClick={() => toggleDiocesis(d.diocesisId)}
          className="col-span-full grid bg-gray-50 hover:bg-gray-100 transition-colors"
          style={{ gridTemplateColumns: GRID_COLS, alignItems: 'center' }}
        >
          <DiocesisNacionalButtonContent
            nombre={d.diocesisNombre}
            tipo={d.diocesisTipo}
            isExpanded={expandedDiocesis.has(d.diocesisId)}
            totalActividades={d.totalActividades}
            totalIngresos={d.totalIngresos}
            totalCostos={d.totalCostos}
            utilidadNeta={d.utilidadNeta}
          />
        </button>

        {expandedDiocesis.has(d.diocesisId) && (
          renderActividadesGrid(p.provinciaId, d.diocesisId)
        )}
      </div>
    </div>
  );

  const renderDiocesisRowGlobal = (d: typeof stats.provincias[0]['diocesis'][0], p: typeof stats.provincias[0]) => (
    <div key={d.diocesisId} className="contents">
      <button
        onClick={() => toggleDiocesis(d.diocesisId)}
        className="col-span-full grid hover:bg-gray-50 transition-colors border-t border-gray-200"
        style={{ gridTemplateColumns: GRID_COLS, alignItems: 'center' }}
      >
        <DiocesisGlobalButtonContent
          nombre={d.diocesisNombre}
          tipo={d.diocesisTipo}
          isExpanded={expandedDiocesis.has(d.diocesisId)}
          totalActividades={d.totalActividades}
          totalIngresos={d.totalIngresos}
          totalCostos={d.totalCostos}
          utilidadNeta={d.utilidadNeta}
        />
      </button>

      {expandedDiocesis.has(d.diocesisId) && (
        renderActividadesGrid(p.provinciaId, d.diocesisId)
      )}
    </div>
  );

  const renderDesglose = () => {
    if (isDiocesano) {
      return (
        <div
          className="grid"
          style={{ gridTemplateColumns: GRID_COLS }}
        >
          <div className="contents">
            <div className="col-span-full grid border-b border-gray-100 bg-gray-50/50" style={{ gridTemplateColumns: GRID_COLS }}>
              <div className="py-2 pl-4" />
              <div className="py-2 text-center">
                <span className="text-xs text-gray-400 flex items-center justify-center gap-1">
                  <Hash className="w-3 h-3" /> Actividades
                </span>
              </div>
              <div className="py-2 text-center">
                <span className="text-xs text-emerald-500 flex items-center justify-center gap-1">
                  <ArrowUpCircle className="w-3 h-3" /> Ingresos
                </span>
              </div>
              <div className="py-2 text-center">
                <span className="text-xs text-red-400 flex items-center justify-center gap-1">
                  <ArrowDownCircle className="w-3 h-3" /> Costos
                </span>
              </div>
              <div className="py-2 pr-4 text-center">
                <span className="text-xs text-blue-500 flex items-center justify-center gap-1">
                  <TrendingUp className="w-3 h-3" /> Utilidad
                </span>
              </div>
            </div>
          </div>
          {renderActividadesGrid(null, null)}
        </div>
      );
    }

    if (isNacional) {
      return (
        <div className="space-y-2">
          {stats.provincias.flatMap(p =>
            p.diocesis.map(d => renderDiocesisCardNacional(d, p))
          )}
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {stats.provincias.map(p => (
          <div key={p.provinciaId} className="border border-gray-200 rounded-lg overflow-hidden">
            <div
              className="grid"
              style={{ gridTemplateColumns: GRID_COLS }}
            >
              <button
                onClick={() => toggleProvincia(p.provinciaId)}
                className="col-span-full grid bg-gray-50 hover:bg-gray-100 transition-colors"
                style={{ gridTemplateColumns: GRID_COLS, alignItems: 'center' }}
              >
                <div className="flex items-center gap-2 py-3 pl-4 min-w-0">
                  {expandedProvincias.has(p.provinciaId)
                    ? <ChevronDown className="w-5 h-5 text-gray-600 flex-shrink-0" />
                    : <ChevronRight className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  }
                  <span className="font-bold text-gray-800 truncate">{p.provinciaNombre}</span>
                </div>
                <div className="text-center py-3 pr-2">
                  <p className="text-xs text-gray-400 flex items-center justify-center gap-0.5 mb-0.5">
                    <Hash className="w-3 h-3" /><span>Actividades</span>
                  </p>
                  <span className="font-bold text-gray-900">{p.totalActividades}</span>
                </div>
                <div className="text-center py-3 pr-2">
                  <p className="text-xs text-emerald-500 flex items-center justify-center gap-0.5 mb-0.5">
                    <ArrowUpCircle className="w-3 h-3" /><span>Ingresos</span>
                  </p>
                  <span className="font-bold text-emerald-700">{formatCurrency(p.totalIngresos)}</span>
                </div>
                <div className="text-center py-3 pr-2">
                  <p className="text-xs text-red-400 flex items-center justify-center gap-0.5 mb-0.5">
                    <ArrowDownCircle className="w-3 h-3" /><span>Costos</span>
                  </p>
                  <span className="font-bold text-red-600">{formatCurrency(p.totalCostos)}</span>
                </div>
                <div className="text-center py-3 pr-4">
                  <p className="text-xs text-blue-500 flex items-center justify-center gap-0.5 mb-0.5">
                    <TrendingUp className="w-3 h-3" /><span>Utilidad</span>
                  </p>
                  <span className={`font-bold ${utilidadColor(p.utilidadNeta)}`}>
                    {formatCurrency(p.utilidadNeta)}
                  </span>
                </div>
              </button>

              {expandedProvincias.has(p.provinciaId) && p.diocesis.map(d =>
                renderDiocesisRowGlobal(d, p)
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderJALsTab = () => (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center gap-3 mb-2">
            <Briefcase className="w-5 h-5 text-gray-500" />
            <p className="text-sm text-gray-500">Total Actividades</p>
          </div>
          <p className="text-3xl font-bold text-gray-900">{totalActividades}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center gap-3 mb-2">
            <ArrowUpCircle className="w-5 h-5 text-emerald-500" />
            <p className="text-sm text-gray-500">Total Ingresos</p>
          </div>
          <p className="text-2xl font-bold text-emerald-700">{formatCurrency(totalIngresos)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center gap-3 mb-2">
            <ArrowDownCircle className="w-5 h-5 text-red-500" />
            <p className="text-sm text-gray-500">Total Costos</p>
          </div>
          <p className="text-2xl font-bold text-red-600">{formatCurrency(totalCostos)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-5 h-5 text-blue-500" />
            <p className="text-sm text-gray-500">Utilidad Neta</p>
          </div>
          <p className={`text-2xl font-bold ${utilidadColor(totalUtilidad)}`}>
            {formatCurrency(totalUtilidad)}
          </p>
        </div>
      </div>

      <AdminFiltersPanel
        filters={filters}
        searchLabel="Buscar actividad"
        searchPlaceholder="Nombre de la actividad..."
        estadoOptions={ESTADO_OPTIONS}
      />

      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4">Desglose por Sede</h2>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            </div>
          )}
          {!loading && stats.provincias.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <Briefcase className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No se encontraron actividades</p>
              {hasActiveFilters && <p className="text-sm mt-1">Intenta cambiar los filtros</p>}
            </div>
          )}
          {!loading && stats.provincias.length > 0 && (
            <div className="p-4">
              {renderDesglose()}
            </div>
          )}
        </div>
      </div>
    </>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'jad':
        if (!usuario?.diocesis_id) {
          return (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
              <p className="text-amber-700 font-medium">Tu usuario no tiene una diócesis asignada</p>
            </div>
          );
        }
        return (
          <ActividadesJAView
            ownerContext={{ tipo: 'diocesis', id: usuario.diocesis_id, nombre: usuario.diocesis?.nombre ?? '' }}
            readOnly={false}
          />
        );

      case 'jan':
        if (!usuario?.provincia_eclesiastica_id) {
          return (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
              <p className="text-amber-700 font-medium">Tu usuario no tiene una provincia eclesiástica asignada</p>
            </div>
          );
        }
        return (
          <ActividadesJAView
            ownerContext={{ tipo: 'provincia', id: usuario.provincia_eclesiastica_id, nombre: '' }}
            readOnly={false}
          />
        );

      case 'jads':
        return (
          <ActividadesJAView
            ownerContext={{ tipo: 'provincia', id: usuario?.provincia_eclesiastica_id ?? 'global', nombre: '' }}
            readOnly={true}
          />
        );

      case 'jans':
        return (
          <ActividadesJAView
            ownerContext={{ tipo: 'global', id: 'global', nombre: '' }}
            readOnly={true}
          />
        );

      case 'jals':
        return renderJALsTab();

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-1">Actividades Económicas</h1>
        <p className="text-gray-500">Vista consolidada de actividades por zona</p>
      </div>

      {/* Tab switcher */}
      <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
        <div className="flex border-b border-slate-200">
          {tabs.map(tab => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`flex-1 px-6 py-4 font-semibold text-sm transition-colors ${
                activeTab === tab
                  ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-700'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              {TAB_LABELS[tab]}
            </button>
          ))}
        </div>

        <div className="p-6 space-y-6">
          {renderTabContent()}
        </div>
      </div>

      <AdminDetalleActividadModal
        isOpen={!!selectedActividad}
        actividadId={selectedActividad?.actividadId ?? ''}
        lumisialId={selectedActividad?.lumisialId ?? ''}
        lumisialNombre={selectedActividad?.lumisialNombre ?? ''}
        lumisialCiudad={selectedActividad?.lumisialCiudad ?? ''}
        diocesisNombre={selectedActividad?.diocesisNombre ?? ''}
        provinciaNombre={selectedActividad?.provinciaNombre ?? ''}
        onClose={() => setSelectedActividad(null)}
      />
    </div>
  );
}
